var _p;
import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import xss from 'xss';
import { Button, DisclaimerText, Heading, Image, Link, LinkIcon, LinkTile } from 'reaxl';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const handlerDefault = () => {};
const idxKey = label => label.replace(/\s/gi, '-');
const handleNavigation = (e, callback, href, index) => {
  e.preventDefault();
  if (typeof callback === 'function') {
    callback(e, index);
  }
  global.open(href);
};
function sanitizeContent(str) {
  return xss(str, {
    stripIgnoreTag: true,
    whiteList: {
      ...xss.whiteList,
      div: [],
      span: [],
      p: [],
      strong: [],
      br: [],
      li: [],
      ul: [],
      sup: []
    }
  });
}
const getCtaButtons = _ref => {
  let {
    ctaButtons,
    handleCtaClick,
    btnWrapperClassNames
  } = _ref;
  return ctaButtons && ctaButtons.map(button => {
    const {
      label = '',
      href
    } = button;
    return /*#__PURE__*/_jsx("div", {
      className: btnWrapperClassNames,
      children: /*#__PURE__*/_jsx(Button, {
        bsStyle: "primary",
        href: href,
        onClick: handleCtaClick,
        children: label
      })
    }, idxKey(label));
  });
};
const getOffsiteLinks = _ref2 => {
  let {
    ctaLinks,
    handleLinkClick,
    rightClassNames
  } = _ref2;
  return ctaLinks && ctaLinks.map((link, index) => {
    const {
      offsite = false,
      label = '',
      href
    } = link;
    let Component = Link;
    let linkProps = {};
    if (offsite) {
      Component = LinkIcon;
      linkProps = {
        target: '_blank',
        rel: 'nofollow noopener'
      };
    }
    return /*#__PURE__*/_jsx("div", {
      className: `margin-top-5 ${rightClassNames}`,
      children: /*#__PURE__*/_jsx(Component, {
        className: "display-block",
        href: href,
        onClick: e => handleNavigation(e, handleLinkClick, href, index),
        ...linkProps,
        children: label
      })
    }, idxKey(label));
  });
};
const renderHeading = (showHeading, title, headingSize, leftClassNames, cpoTile, showTile) => {
  const headingClassNames = clsx({
    'margin-bottom-2': true
  }, leftClassNames);
  const certText = 'Certified Pre-Owned';
  const headingProps = [1, 2, 3, 4].includes(headingSize) ? {
    level: headingSize
  } : {
    size: headingSize
  };
  return showHeading && /*#__PURE__*/_jsx("div", {
    className: showTile && cpoTile ? headingClassNames : 'col-xs-12',
    children: /*#__PURE__*/_jsx(Heading, {
      ...headingProps,
      children: title || certText
    })
  });
};
const renderHeadline = (subheading, heading, headlineClassNames) => {
  const headline = subheading || heading;
  return headline && /*#__PURE__*/_jsx("div", {
    className: headlineClassNames,
    children: headline
  });
};

// render Certified Tile
const renderTile = (cpoTile, layoutPortrait, rightClassNames) => cpoTile && /*#__PURE__*/_jsx("div", {
  className: rightClassNames,
  children: /*#__PURE__*/_jsx(Image, {
    className: clsx('w-auto', {
      'padding-top-2': layoutPortrait
    }),
    src: typeof cpoTile === 'string' ? cpoTile : cpoTile.src // Collections data is shaped differently than SRP/VDP data (see mock data for example)
    ,
    alt: cpoTile.alt || 'logo',
    height: "30px",
    width: "110px"
  })
});
const renderDisclaimer = (disclaimer, leftClassNames, showCtaLinks, isTileHeading) => disclaimer && /*#__PURE__*/_jsxs(DisclaimerText, {
  componentClass: "div",
  className: clsx('margin-top-4', {
    [leftClassNames]: showCtaLinks || isTileHeading,
    'col-xs-12': !(showCtaLinks || isTileHeading)
  }),
  children: [_p || (_p = /*#__PURE__*/_jsx("p", {
    children: "Disclaimer"
  })), /*#__PURE__*/_jsx("div", {
    dangerouslySetInnerHTML: {
      __html: sanitizeContent(disclaimer)
    }
  })]
});
const renderInspectionLink = (inspectionLink, onInspectionLinkClick, hasCtaButtons) => inspectionLink && inspectionLink.link && /*#__PURE__*/_jsx(LinkTile, {
  className: clsx({
    'margin-left-5': hasCtaButtons
  }, 'text-size-400 margin-vertical-5'),
  image: {
    src: inspectionLink.img
  },
  link: {
    href: inspectionLink.link,
    label: inspectionLink.text,
    offsite: inspectionLink.offsite
  },
  onLinkClick: onInspectionLinkClick,
  onTileClick: onInspectionLinkClick
});

/**
 *  Certified Program Offer (CPO) component will help to standardize look & feels across Apps.
 */
const CertifiedProgramOffer = /*#__PURE__*/memo(_ref3 => {
  let {
    className,
    ctaWrapperClassNames,
    data = {},
    headingSize = 600,
    isTileHeading = false,
    onLinkClick = handlerDefault,
    onCtaClick = handlerDefault,
    onInspectionLinkClick = handlerDefault,
    orientation = 'portrait',
    showHeading = true,
    showTile = true,
    showCtaLinks = true,
    ...rest
  } = _ref3;
  const handleCtaClick = useCallback(event => {
    onCtaClick(event);
  }, [onCtaClick]);
  const handleLinkClick = useCallback((event, index) => {
    onLinkClick(event, index);
  }, [onLinkClick]);
  const handleInspectionLinkClick = useCallback(event => {
    onInspectionLinkClick(event);
  }, [onInspectionLinkClick]);
  const {
    heading = '',
    subheading = '',
    cpoTile = {},
    content = '',
    ctaButtons = [],
    ctaLinks = [],
    disclaimer = '',
    title = '',
    inspectionLink = {}
  } = data;
  const layoutPortrait = orientation === 'portrait';
  const leftClassNames = clsx({
    'col-xs-12': layoutPortrait && !isTileHeading,
    'col-xs-8': !layoutPortrait && !isTileHeading
  });
  const rightClassNames = clsx({
    'col-xs-12': layoutPortrait && !isTileHeading,
    'col-xs-4': !layoutPortrait && !isTileHeading,
    'text-right': !layoutPortrait && !isTileHeading,
    'margin-bottom-2': isTileHeading
  });

  // Allows flexibility to position the button left, right, center
  const btnWrapperClassNames = clsx({
    'margin-top-5': true
  }, ctaWrapperClassNames);
  const tileClassNames = isTileHeading ? 'col-md-2' : 'row';
  const containerClassNames = isTileHeading ? 'col-md-10' : 'row';
  const contentClassNames = showCtaLinks || isTileHeading ? leftClassNames : 'col-xs-12';
  const ctaClassNames = 'display-flex flex-column flex-sm-row';
  const headlineClassNames = clsx({
    'text-bold text-size-300': true,
    'margin-top-3 col-xs-12 margin-bottom-5': !isTileHeading,
    'margin-top-1 margin-bottom-2': isTileHeading
  });
  const renderContent = /*#__PURE__*/_jsx("div", {
    className: contentClassNames
    // eslint-disable-next-line react/no-danger
    ,
    dangerouslySetInnerHTML: {
      __html: sanitizeContent(content)
    }
  });
  const renderCtas = inspectionLink ? /*#__PURE__*/_jsxs("div", {
    className: ctaClassNames,
    children: [getCtaButtons({
      ctaButtons,
      handleCtaClick,
      btnWrapperClassNames
    }), renderInspectionLink(inspectionLink, handleInspectionLinkClick, !!ctaButtons && !!ctaButtons.length)]
  }) : getCtaButtons({
    ctaButtons,
    handleCtaClick,
    btnWrapperClassNames
  });
  return /*#__PURE__*/_jsxs("div", {
    className: className,
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      className: tileClassNames,
      children: [!isTileHeading && renderHeading(showHeading, title, headingSize, leftClassNames, cpoTile, showTile), showTile && renderTile(cpoTile, layoutPortrait, rightClassNames)]
    }), /*#__PURE__*/_jsxs("div", {
      className: containerClassNames,
      children: [renderHeadline(subheading, heading, headlineClassNames), renderContent, renderDisclaimer(disclaimer, leftClassNames, showCtaLinks, isTileHeading), getOffsiteLinks({
        ctaLinks,
        handleLinkClick,
        rightClassNames
      }), isTileHeading && renderCtas]
    }), !isTileHeading && renderCtas]
  });
});
export default CertifiedProgramOffer;